// Copyright (c) 2024 by Audere

import { TextLabelerApiResult, apiSlice } from "./apiSlice";

import { usesStagingApi } from "../../utils/config";

const apiSliceWithTagTypes = apiSlice.enhanceEndpoints({
  addTagTypes: ["Conversations", "Labels"],
});

export type FormLabelData = Record<string, unknown>;

type StructuredData = Record<string, FieldInfo>;

export enum LabelingRole {
  default = "unknown",
  conversation_labeler = "conversation_labeler",
  conversation_label_qc = "conversation_label_qc",
  conversation_label_monitor = "conversation_label_monitor",
}

export enum Priority {
  high = "high",
  standard = "standard",
}

export enum LabelingStatus {
  not_started = "not_started",
  label_in_progress = "label_in_progress",
  label_complete = "label_complete",
  qc_in_progress = "qc_in_progress",
  qc_complete = "qc_complete",
  qc_not_started = "qc_not_started",
}

export enum MessageRole {
  user = "user",
  assistant = "assistant",
}

export enum MessageType {
  text = "text",
  // To add later:
  // image = "image",
  // video = "video",
}

export enum HivInfoSource {
  self_test = "self_test",
  client_reported = "client_reported",
}

export enum TestResult {
  positive = "positive",
  negative = "negative",
  invalid = "invalid",
  uninterpretable = "uninterpretable",
}

export enum DirectionOfTranslation {
  nativeToInternal = "native_to_internal",
  internalToNative = "internal_to_native",
}

export interface GetRolesDataResponse {
  groups: Record<
    string,
    {
      roles: LabelingRole[];
    }
  >;
}

export interface UserData {
  email: string;
  name: string;
  picture: string | null;
}

export interface LabelerData extends UserData {
  labeler_uid: string;
  labeler_status: LabelingStatus;
}

export interface QCData extends UserData {
  labeler_uid: string;
  qc_uid: string;
  qc_status: LabelingStatus;
}

export interface LabelerConversationData {
  conversation_uid: string;
  start_timestamp: string;
  priority: Priority;
  labelers: LabelerData[];
}

export interface QCConversationData {
  conversation_uid: string;
  start_timestamp: string;
  priority: Priority;
  labelers: LabelerData[];
  qc: QCData[];
}

export interface MonitorConversationData {
  conversation_uid: string;
  start_timestamp: string;
  priority: Priority;
  labelers: LabelerData[];
  qc: QCData[];
}

// Message data structure
export interface MessageData {
  message_uid: string;
  role: MessageRole;
  type: MessageType;
  text: string;
  sent_ts: string;
  in_reply_to: {
    message_id: string | null;
  };
  translation?: {
    native_text: string;
    internal_text: string;
    direction_of_translation: DirectionOfTranslation;
    source_language: string;
    target_language: string;
    translation_timestamp: string;
  };
  // To be added in the future
  // reactions?: { role: MessageRole; emoji: string }[];
}

export interface FieldInfo {
  description: string;
  value: string | string[];
  possible_responses?: string[];
}

export interface LabelData {
  label_uid: string;
  message_uid?: string;
  labeling_type: string;
  labeling_task: string;
  label_data: FormLabelData;
  qc_data?: FormLabelData;
  labeled_content?: Record<string, string | string[] | FieldInfo> | null;
  labeling_schema_version?: string;
}

export interface HivInfo {
  client_reported_hiv_status: string | null;
  source: HivInfoSource;
  time_ago: string;
  result: TestResult;
}

export interface PreviousConvoSummary {
  timestamp: string;
  text: string;
}

interface GetConversationsRequest {
  group: string;
  labeling_type: string;
  offset: number;
  limit: number;
}

export interface GetQCConversationsResponse {
  conversations: QCConversationData[];
  offset: number;
  end_of_list: boolean;
}

export interface GetSampledConversationsResponse {
  conversations: MonitorConversationData[];
  offset: number;
  end_of_list: boolean;
}

export interface GetLabelerConversationsResponse {
  conversations: LabelerConversationData[];
  offset: number;
  end_of_list: boolean;
}

interface GetConversationRequest {
  group: string;
  conversation_uid: string;
  labeling_type: string;
  message_cursor: string | null;
  limit: number;
}

export interface GetConversationResponse {
  conversation_uid: string;
  last_message_recorded_ts: string;
  messages: MessageData[];
  flags: string[];
  labelers: LabelerData[];
  qc?: QCData[];
  priority: Priority;
  start_timestamp: string;
  end_cursor: string | null;
  persona?: string;
}

interface GetSummaryRequest {
  group: string;
  conversation_uid: string;
}

export interface GetSummaryResponse {
  conversation_uid: string;
  summary: string;
}

export interface GetStructuredDataRequest {
  group: string;
  conversation_uid: string;
}

export interface GetStructuredDataResponse {
  conversation_uid: string;
  structured_data: StructuredData;
}

export interface GetLabelsRequest {
  group: string;
  conversation_uid: string;
  labeler_uid?: string;
  qc_uid?: string;
  label_cursor: string | null;
  limit: number;
}

export interface GetLabelsResponse {
  conversation_uid: string;
  labels: LabelData[];
  start_cursor: string | null;
}

export interface AddLabelRequest {
  group: string;
  conversation_uid: string;
  message_uid?: string;
  labeling_type: string;
  labeling_task: string;
  labeling_schema_version: string;
  label_data: FormLabelData;
  labeled_content?: StructuredData | string;
}

export interface UpdateLabelRequest {
  group: string;
  conversation_uid: string;
  labeling_type: string;
  label_uid: string;
  message_uid?: string;
  label_data: FormLabelData;
}

export interface UpdateLabelingStatusRequest {
  group: string;
  conversation_uid: string;
  labeling_type: string;
  labeler_uid?: string; // Needed when logged in user is QC
  status: LabelingStatus;
}

export interface AddQCFeedbackRequest {
  group: string;
  conversation_uid: string;
  labeler_uid: string;
  label_uid: string;
  message_uid?: string;
  qc_data: Record<string, unknown>;
}

export interface GetConversationPatientSnapshotRequest {
  group: string;
  conversation_uid: string;
}

type SymptomItem = {
  symptom: string;
  characteristics: string;
  still_active: boolean;
  onset_timing: string;
  cause: string;
  location: string | null;
  reported_for: string;
  collected_at: string | null;
};

export interface PatientProfileItem {
  value: boolean | number | string | string[] | null | SymptomItem[];
  description: string;
}

export interface GetConversationPatientSnapshotResponse {
  patient_uid: string;
  preferred_name: string;
  saved_timestamp: string;
  hiv_info: HivInfo | null;
  patient_profile: Record<string, PatientProfileItem> | null;
  previous_conversation_summary: PreviousConvoSummary | null;
}

const logRawResult = (
  query: string,
  rawResult: TextLabelerApiResult<unknown>
) => {
  if (usesStagingApi()) console.log(`Raw result from ${query}:\n\n`, rawResult);
};

export const labelApiSlice = apiSliceWithTagTypes.injectEndpoints({
  endpoints: (builder) => ({
    getRoles: builder.query<GetRolesDataResponse, string>({
      query: (token: string) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_labeler_roles",
          input: {},
        }),
        headers: [["X-Audere-API-Token", token]],
      }),
      transformResponse: (
        rawResult: TextLabelerApiResult<GetRolesDataResponse>
      ) => {
        logRawResult("get_labeler_roles", rawResult);
        return rawResult.output;
      },
    }),

    getQCConversations: builder.query<
      GetQCConversationsResponse,
      GetConversationsRequest
    >({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_qc_conversations",
          input: {
            group: arg.group,
            labeling_type: arg.labeling_type,
            offset: arg.offset,
            limit: arg.limit,
          },
        }),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.conversations.map(({ conversation_uid }) => ({
                type: "Conversations" as const,
                id: conversation_uid,
              })),
              { type: "Conversations", id: "LIST" },
            ]
          : [{ type: "Conversations", id: "LIST" }],
      transformResponse: (
        rawResult: TextLabelerApiResult<GetQCConversationsResponse>
      ) => {
        logRawResult("get_qc_conversations", rawResult);
        return rawResult.output;
      },
    }),

    getLabelerConversations: builder.query<
      GetLabelerConversationsResponse,
      GetConversationsRequest
    >({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_labeling_conversations",
          input: {
            group: arg.group,
            labeling_type: arg.labeling_type,
            offset: arg.offset,
            limit: arg.limit,
          },
        }),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.conversations.map(({ conversation_uid }) => ({
                type: "Conversations" as const,
                id: conversation_uid,
              })),
              { type: "Conversations", id: "LIST" },
            ]
          : [{ type: "Conversations", id: "LIST" }],
      transformResponse: (
        rawResult: TextLabelerApiResult<GetLabelerConversationsResponse>
      ) => {
        logRawResult("get_labeling_conversations", rawResult);
        return rawResult.output;
      },
    }),

    getSummary: builder.query<GetSummaryResponse, GetSummaryRequest>({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_summary",
          input: {
            group: arg.group,
            conversation_uid: arg.conversation_uid,
          },
        }),
      }),
      transformResponse: (
        rawResult: TextLabelerApiResult<GetSummaryResponse>
      ) => {
        logRawResult("get_summary", rawResult);
        return rawResult.output;
      },
    }),

    getStructuredData: builder.query<
      GetStructuredDataResponse,
      GetStructuredDataRequest
    >({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_structured_data",
          input: {
            group: arg.group,
            conversation_uid: arg.conversation_uid,
          },
        }),
      }),
      transformResponse: (
        rawResult: TextLabelerApiResult<GetStructuredDataResponse>
      ) => {
        logRawResult("get_structured_data", rawResult);
        return rawResult.output;
      },
    }),

    getLabels: builder.query<GetLabelsResponse, GetLabelsRequest>({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_labels",
          input: {
            group: arg.group,
            conversation_uid: arg.conversation_uid,
            labeler_uid: arg.labeler_uid,
            label_cursor: arg.label_cursor,
            limit: arg.limit,
          },
        }),
      }),
      // Provide a list tag specific to the conversation
      providesTags: (result, _, arg) =>
        result
          ? [
              ...result.labels.map(({ label_uid }) => ({
                type: "Labels" as const,
                id: label_uid,
              })),
              { type: "Labels", id: `${arg.conversation_uid}-LIST` },
            ]
          : [{ type: "Labels", id: `${arg.conversation_uid}-LIST` }],
      transformResponse: (
        rawResult: TextLabelerApiResult<GetLabelsResponse>
      ) => {
        logRawResult("get_labels", rawResult);
        return rawResult.output;
      },
    }),

    addLabel: builder.mutation<string, AddLabelRequest>({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "add_label",
          input: {
            group: arg.group,
            conversation_uid: arg.conversation_uid,
            message_uid: arg.message_uid,
            labeling_type: arg.labeling_type,
            labeling_task: arg.labeling_task,
            labeling_schema_version: arg.labeling_schema_version,
            label_data: arg.label_data,
            ...(arg.labeled_content !== undefined && {
              labeled_content: arg.labeled_content,
            }),
          },
        }),
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          labelApiSlice.util.updateQueryData(
            "getLabels",
            {
              group: arg.group,
              conversation_uid: arg.conversation_uid,
              label_cursor: null,
              limit: 1000,
            },
            (draftLabel) => {
              draftLabel.labels.push({
                label_uid: "*",
                message_uid: arg.message_uid,
                labeling_type: arg.labeling_type,
                labeling_task: arg.labeling_task,
                label_data: arg.label_data,
              });
            }
          )
        );
        try {
          const { data: newLabel } = await queryFulfilled;

          dispatch(
            labelApiSlice.util.updateQueryData(
              "getLabels",
              {
                group: arg.group,
                conversation_uid: arg.conversation_uid,
                label_cursor: null,
                limit: 1000,
              },
              (draftLabel) => {
                const placeholder = draftLabel.labels.find(
                  (l) => l.label_uid === "*"
                );
                if (placeholder) {
                  Object.assign(placeholder, {
                    ...placeholder,
                    label_uid: newLabel,
                  });
                }
              }
            )
          );
        } catch {
          patchResult.undo();
        }
      },
      transformResponse: (
        rawResult: TextLabelerApiResult<{ label_uid: string }>
      ) => {
        logRawResult("add_label", rawResult);
        return rawResult.output.label_uid;
      },
    }),

    updateLabel: builder.mutation<string, UpdateLabelRequest>({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "update_label",
          input: {
            group: arg.group,
            conversation_uid: arg.conversation_uid,
            labeling_type: arg.labeling_type,
            label_uid: arg.label_uid,
            label_data: arg.label_data,
          },
        }),
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          labelApiSlice.util.updateQueryData(
            "getLabels",
            {
              group: arg.group,
              conversation_uid: arg.conversation_uid,
              label_cursor: null,
              limit: 1000,
            },
            (draftLabel) => {
              // Find the label in the list and update it
              const label = draftLabel.labels.find(
                (l) => l.label_uid === arg.label_uid
              );
              if (label) label.label_data = arg.label_data;
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      transformResponse: (
        rawResult: TextLabelerApiResult<{ label_uid: string }>
      ) => {
        logRawResult("update_label", rawResult);
        return rawResult.output.label_uid;
      },
    }),

    updateLabelingStatus: builder.mutation<string, UpdateLabelingStatusRequest>(
      {
        query: (arg) => ({
          url: "/text_labeler",
          method: "POST",
          body: JSON.stringify({
            method_id: "update_labeling_status",
            input: {
              group: arg.group,
              conversation_uid: arg.conversation_uid,
              labeling_type: arg.labeling_type,
              labeler_uid: arg.labeler_uid,
              status: arg.status,
            },
          }),
        }),
        // Invalidate only the conversation that was updated
        invalidatesTags: (result, error, arg) => [
          { type: "Conversations", id: arg.conversation_uid },
        ],
        transformResponse: (
          rawResult: TextLabelerApiResult<{ conversation_uid: string }>
        ) => {
          logRawResult("update_labeling_status", rawResult);
          return rawResult.output.conversation_uid;
        },
      }
    ),

    addQCFeedback: builder.mutation<string, AddQCFeedbackRequest>({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "add_qc_feedback",
          input: {
            group: arg.group,
            conversation_uid: arg.conversation_uid,
            labeler_uid: arg.labeler_uid,
            label_uid: arg.label_uid,
            qc_data: arg.qc_data,
          },
        }),
      }),
      // Invalidate only the label that was updated
      invalidatesTags: (result, error, arg) => [
        { type: "Labels", id: arg.label_uid },
      ],
      transformResponse: (
        rawResult: TextLabelerApiResult<{ label_uid: string }>
      ) => {
        logRawResult("add_qc_feedback", rawResult);
        return rawResult.output.label_uid;
      },
    }),

    getConversationPatientSnapshot: builder.query<
      GetConversationPatientSnapshotResponse,
      GetConversationPatientSnapshotRequest
    >({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_conversation_patient_snapshot",
          input: {
            group: arg.group,
            conversation_uid: arg.conversation_uid,
          },
        }),
      }),
      transformResponse: (
        rawResult: TextLabelerApiResult<GetConversationPatientSnapshotResponse>
      ) => {
        logRawResult("get_conversation_patient_snapshot", rawResult);
        return rawResult.output;
      },
    }),
    getConversationForLabeler: builder.query<
      GetConversationResponse,
      GetConversationRequest
    >({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_conversation_for_labeler",
          input: {
            group: arg.group,
            conversation_uid: arg.conversation_uid,
            labeling_type: arg.labeling_type,
            message_cursor: arg.message_cursor,
            limit: arg.limit,
          },
        }),
      }),
      transformResponse: (
        rawResult: TextLabelerApiResult<GetConversationResponse>
      ) => {
        logRawResult("get_conversation_for_labeler", rawResult);
        return rawResult.output;
      },
    }),
    getConversationForQC: builder.query<
      GetConversationResponse,
      GetConversationRequest
    >({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_conversation_for_qc",
          input: {
            group: arg.group,
            conversation_uid: arg.conversation_uid,
            labeling_type: arg.labeling_type,
            message_cursor: arg.message_cursor,
            limit: arg.limit,
          },
        }),
      }),
      transformResponse: (
        rawResult: TextLabelerApiResult<GetConversationResponse>
      ) => {
        logRawResult("get_conversation_for_qc", rawResult);
        return rawResult.output;
      },
    }),

    getSampledConversations: builder.query<
      GetSampledConversationsResponse,
      GetConversationsRequest
    >({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_monitor_conversations",
          input: {
            group: arg.group,
            labeling_type: arg.labeling_type,
            offset: arg.offset,
            limit: arg.limit,
          },
        }),
      }),
      transformResponse: (
        rawResult: TextLabelerApiResult<GetQCConversationsResponse>
      ) => {
        logRawResult("get_monitor_conversations", rawResult);
        return rawResult.output;
      },
    }),

    getConversationForMonitor: builder.query<
      GetConversationResponse,
      GetConversationRequest
    >({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_conversation_for_monitor",
          input: {
            group: arg.group,
            conversation_uid: arg.conversation_uid,
            labeling_type: arg.labeling_type,
            message_cursor: arg.message_cursor,
            limit: arg.limit,
          },
        }),
      }),
      transformResponse: (
        rawResult: TextLabelerApiResult<GetConversationResponse>
      ) => {
        logRawResult("get_conversation_for_monitor", rawResult);
        return rawResult.output;
      },
    }),

    getLabelsForMonitor: builder.query<GetLabelsResponse, GetLabelsRequest>({
      query: (arg) => ({
        url: "/text_labeler",
        method: "POST",
        body: JSON.stringify({
          method_id: "get_labels_for_monitor",
          input: {
            group: arg.group,
            conversation_uid: arg.conversation_uid,
            labeler_uid: arg.labeler_uid,
            qc_uid: arg.qc_uid,
            label_cursor: arg.label_cursor,
            limit: arg.limit,
          },
        }),
      }),
      transformResponse: (
        rawResult: TextLabelerApiResult<GetLabelsResponse>
      ) => {
        logRawResult("get_labels_monitor", rawResult);
        return rawResult.output;
      },
    }),
  }),
});

export const {
  useLazyGetRolesQuery,
  useGetQCConversationsQuery,
  useGetLabelerConversationsQuery,
  useGetSummaryQuery,
  useGetStructuredDataQuery,
  useGetLabelsQuery,
  useAddLabelMutation,
  useUpdateLabelMutation,
  useUpdateLabelingStatusMutation,
  useAddQCFeedbackMutation,
  useGetConversationPatientSnapshotQuery,
  useGetConversationForLabelerQuery,
  useGetConversationForQCQuery,
  useGetSampledConversationsQuery,
  useGetConversationForMonitorQuery,
  useGetLabelsForMonitorQuery,
} = labelApiSlice;
